//	Nice ticks (1-2-5 stepping) and x-label thinning.

export function niceStep(span, count) {
	const raw = span / Math.max(1, count);
	const mag = 10 ** Math.floor(Math.log10(raw));
	const norm = raw / mag;
	//	d3-style thresholds (sqrt of the neighbouring steps) so 0–13.8 at 5 ticks steps by 2, not 5
	const step = norm < 1.4142 ? 1 : norm < 3.1623 ? 2 : norm < 7.0711 ? 5 : 10;
	return step * mag;
}

//	ticks that cover [min, max] with nice bounds; zero is included when `includeZero`
export function niceTicks(min, max, count = 5, includeZero = false) {
	if (!Number.isFinite(min) || !Number.isFinite(max)) {
		min = 0;
		max = 1;
	}
	if (min > max) [min, max] = [max, min];
	count = Number.isFinite(count) ? Math.max(1, Math.min(100, Math.floor(count))) : 5;
	if (includeZero) {
		min = Math.min(min, 0);
		max = Math.max(max, 0);
	}
	if (min === max) {
		if (min === 0) max = 1;
		else if (min > 0) min = 0;
		else max = 0;
	}
	// A step smaller than the representable spacing at this magnitude cannot advance a
	// floating-point counter. Keep a finite iteration budget even for hostile input.
	const step = Math.max(niceStep(max - min, count) || 0, Math.max(Math.abs(min), Math.abs(max)) * Number.EPSILON, Number.MIN_VALUE);
	if (!Number.isFinite(step)) return { ticks: [min, 0, max], step: Math.max(Math.abs(min), Math.abs(max)), min, max };
	//	a small negative overshoot (|min| < step) snaps to a fifth of a step so -0.3 does not drag a
	//	0–20 axis down to -5; ticks themselves stay on whole steps inside the bounds
	const sub = min < 0 && min > -step ? step / 5 : step;
	const lo = Math.min(min, round(Math.floor(min / sub + 1e-9) * sub, sub));
	const hi = Math.max(max, round(Math.ceil(max / step - 1e-9) * step, step));
	const first = Math.ceil(lo / step - 1e-9) * step;
	const ticks = [];
	for (let i = 0; i < count * 3 + 4; i++) {
		const v = round(first + i * step, step);
		if (!Number.isFinite(v) || v > hi + step / 2) break;
		if (v >= lo && v <= hi && (!ticks.length || v > ticks[ticks.length - 1])) ticks.push(v);
	}
	if (!ticks.length) ticks.push(min, max);
	return { ticks, step, min: Number.isFinite(lo) ? lo : min, max: Number.isFinite(hi) ? hi : max };
}

function round(v, step) {
	const decimals = Math.max(0, -Math.floor(Math.log10(step)) + 1);
	return decimals <= 100 ? Number(v.toFixed(decimals)) : v;
}

export function tickCount(plotHeight) {
	return Math.max(3, Math.min(8, Math.round(plotHeight / 60)));
}

//	indices of x labels that fit: every k-th label so none overlap, always including the first and last when possible
export function thinLabels(n, plotWidth, maxLabelPx) {
	if (n <= 0) return [];
	const k = Math.max(1, Math.ceil((n * (maxLabelPx + 8)) / Math.max(1, plotWidth)));
	const out = [];
	for (let i = 0; i < n; i += k) out.push(i);
	const last = n - 1;
	if (out[out.length - 1] !== last) {
		//	replace the previous one if the last would collide with it
		if ((last - out[out.length - 1]) * (plotWidth / n) < maxLabelPx + 8) out.pop();
		out.push(last);
	}
	return out;
}
